/**
 * Cart reservation timer + free shipping bar.
 * - Timer: 10:00 starting from first add-to-cart, persisted in localStorage.
 *   Resets to a fresh window when the cart goes empty.
 * - Shipping bar: shows progress toward 50€ when there's a physical item in cart.
 *   Reacts to cart:update events.
 *
 * Heavy work (fetching /cart.js, querying DOM) only runs in response to the
 * cart:update event — never on arbitrary DOM mutations.
 */

const STORAGE_KEY = 'cart_reservation_start';
const DURATION_MS = 10 * 60 * 1000;
const TICK_MS = 1000;

function readStart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) ? n : null;
  } catch (_) {
    return null;
  }
}

function writeStart(ts) {
  try {
    localStorage.setItem(STORAGE_KEY, String(ts));
  } catch (_) {}
}

function clearStart() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_) {}
}

function formatMMSS(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

let cartPromise = null;
async function fetchCartState() {
  if (cartPromise) return cartPromise;
  cartPromise = fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then((r) => r.json())
    .catch(() => null)
    .finally(() => {
      cartPromise = null;
    });
  return cartPromise;
}

let timerInterval = null;

function getBanners() {
  return document.querySelectorAll('[data-cart-reservation]');
}

function renderTimer() {
  const start = readStart();
  const banners = getBanners();
  if (!start || banners.length === 0) {
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
    return;
  }

  const remaining = start + DURATION_MS - Date.now();
  const expired = remaining <= 0;

  banners.forEach((banner) => {
    const textEl = banner.querySelector('[data-cart-reservation-text]');
    if (!textEl) return;

    banner.removeAttribute('hidden');

    if (expired) {
      banner.setAttribute('data-expired', 'true');
      textEl.textContent = banner.dataset.expiredText || 'Tu reserva ha expirado';
    } else {
      banner.removeAttribute('data-expired');
      const prefix = banner.dataset.activePrefix || 'Carrito reservado por';
      textEl.textContent = `${prefix} ${formatMMSS(remaining)}`;
    }
  });

  if (expired && timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
}

function ensureTimerRunning() {
  if (timerInterval) return;
  renderTimer();
  timerInterval = setInterval(renderTimer, TICK_MS);
}

function hideAllBanners() {
  getBanners().forEach((el) => {
    el.setAttribute('hidden', '');
    el.removeAttribute('data-expired');
  });
}

async function syncReservation() {
  const cart = await fetchCartState();
  if (!cart) return;

  if ((cart.item_count || 0) === 0) {
    clearStart();
    hideAllBanners();
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
    return;
  }

  if (!readStart()) writeStart(Date.now());
  ensureTimerRunning();
}

async function syncShippingBars() {
  const bars = document.querySelectorAll('[data-cart-shipping-bar]');
  if (bars.length === 0) return;

  const cart = await fetchCartState();
  if (!cart) return;

  const hasPhysical = (cart.items || []).some((it) => it.requires_shipping);

  bars.forEach((bar) => {
    const alwaysVisible = bar.dataset.alwaysVisible === 'true';
    if (!alwaysVisible && !hasPhysical) {
      bar.setAttribute('hidden', '');
      return;
    }
    bar.removeAttribute('hidden');

    const thresholdCents = parseInt(bar.dataset.threshold || '5000', 10);
    const total = cart.total_price || 0;
    const remaining = Math.max(0, thresholdCents - total);
    const progress = Math.min(100, Math.round((total / thresholdCents) * 100));

    const fill = bar.querySelector('[data-cart-shipping-fill]');
    if (fill) fill.style.width = `${progress}%`;

    const label = bar.querySelector('[data-cart-shipping-label]');
    if (label) {
      if (remaining === 0) {
        label.textContent = bar.dataset.reachedText || '¡Envío gratis conseguido!';
      } else {
        const template = bar.dataset.progressText || 'Te faltan {amount} para envío gratis';
        const formatted = (remaining / 100).toLocaleString('es-ES', {
          style: 'currency',
          currency: cart.currency || 'EUR',
          minimumFractionDigits: 2,
        });
        label.textContent = template
          .replace('{{ amount }}', formatted)
          .replace('{amount}', formatted);
      }
    }
  });
}

function syncAll() {
  syncReservation();
  syncShippingBars();
}

// Delegación en `document`: el handler vive en un nodo estable. No importa
// cuándo aparezca el botón ni cuántas veces el drawer se re-renderice — el
// click siempre burbujea hasta acá. Antes esto se bindeaba por nodo y
// fallaba en el primer add-to-cart porque el snippet `cart-upsell` no estaba
// en el DOM (sólo se renderiza con cart no vacío) y al re-renderizarse vía
// cart:update el rebind corría antes de que la nueva HTML estuviese montada.
let upsellInFlight = false;

async function handleUpsellClick(btn) {
  if (upsellInFlight) return;
  const variantId = btn.dataset.variantId;
  if (!variantId) return;

  upsellInFlight = true;
  btn.disabled = true;

  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
  });

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: Array.from(sectionIds).join(','),
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) throw new Error('add failed');
    const result = await res.json();

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          sourceId: 'cart-upsell',
          data: {
            source: 'cart-upsell',
            itemCount: 1,
            variantId,
            sections: result.sections,
          },
        },
      })
    );
  } catch (_) {
    btn.disabled = false;
  } finally {
    upsellInFlight = false;
  }
}

function bindUpsellDelegation() {
  if (document.body.dataset.cartUpsellDelegationBound === 'true') return;
  document.body.dataset.cartUpsellDelegationBound = 'true';

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-cart-upsell-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    handleUpsellClick(btn);
  });
}

function init() {
  syncAll();
  bindUpsellDelegation();
  document.addEventListener('cart:update', () => {
    syncAll();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
